import { useEffect, useRef } from 'react';
import { RING_TIMEOUT_MS } from '@etown/shared';
import { ANCHOR_BUBBLE } from '../../game/overlayAnchors';
import { useOverlayAnchor } from '../../hooks/useOverlayAnchor';
import { RING_DECLINED_RESET_MS } from '../../lib/constants';
import { useMediaStore, type PeerMedia } from '../../stores/mediaStore';
import { usePlayersStore } from '../../stores/playersStore';
import { localMedia } from '../../webrtc/localMedia';
import { useOffice } from '../office/OfficeContext';
import './av.css';

/**
 * Schwebende Video-Bubbles ueber den Avataren. Bewusst DOM statt Canvas:
 * native Video-Dekodierung, CSS-Styling, und die Position schreibt der
 * OverlayProjector pro Frame als transform (compositor-freundlich).
 *
 * Peers ohne Medien-Freigabe (pending) zeigen stattdessen die Klingel-UI.
 * Waehrend das CallPanel offen ist, uebernimmt es die Video-Darstellung —
 * hier bleiben nur die pending-Bubbles sichtbar.
 */
export function VideoBubbleLayer() {
  const peers = useMediaStore((s) => s.peers);
  const camLive = useMediaStore((s) => s.camLive);
  const callPanelOpen = useMediaStore((s) => s.callPanelOpen);
  const selfId = usePlayersStore((s) => s.selfId);

  return (
    <>
      {selfId && camLive && !callPanelOpen && (
        <VideoBubble key="self" playerId={selfId} stream={localMedia.getStream('cam')} label="Du" />
      )}
      {Object.values(peers).map((peer) =>
        peer.mediaActive ? (
          !callPanelOpen && (
            <VideoBubble
              key={peer.peerId}
              playerId={peer.peerId}
              stream={peer.streams.cam ?? null}
              label={peer.displayName}
              connecting={peer.connection === 'connecting'}
              audioOnly={!peer.streams.cam && !!peer.streams.mic}
            />
          )
        ) : (
          <RingBubble key={peer.peerId} peer={peer} />
        ),
      )}
    </>
  );
}

interface VideoBubbleProps {
  playerId: string;
  stream: MediaStream | null;
  label: string;
  connecting?: boolean;
  audioOnly?: boolean;
}

function VideoBubble({ playerId, stream, label, connecting = false, audioOnly = false }: VideoBubbleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  useOverlayAnchor(ref, { kind: 'player', id: playerId }, ANCHOR_BUBBLE);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  // Ohne Inhalt keine Bubble (nur verbinden/Audio zeigt einen Status-Punkt)
  if (!stream && !connecting && !audioOnly) return null;

  return (
    <div ref={ref} className="bubble" data-player={playerId}>
      {stream ? (
        // Audio laeuft separat ueber WebAudio (Spatial-Gain) — Video stumm
        <video ref={videoRef} className="bubble__video" autoPlay playsInline muted />
      ) : (
        <div className={`bubble__placeholder ${connecting ? 'bubble__placeholder--connecting' : ''}`}>
          {connecting ? '…' : '🎙️'}
        </div>
      )}
      <span className="bubble__label">{label}</span>
    </div>
  );
}

/** Bubble eines pending-Peers: Anklingeln / Klingelt… / Abgelehnt. */
function RingBubble({ peer }: { peer: PeerMedia }) {
  const ref = useRef<HTMLDivElement>(null);
  const { socket } = useOffice();
  const setPeerRing = useMediaStore((s) => s.setPeerRing);
  useOverlayAnchor(ref, { kind: 'player', id: peer.peerId }, ANCHOR_BUBBLE);

  // Auto-Ablauf des eigenen Klingelversuchs bzw. der Abgelehnt-Anzeige
  useEffect(() => {
    if (peer.ring === 'outgoing') {
      const timer = window.setTimeout(() => {
        socket.emit('call:cancel', { to: peer.peerId });
        setPeerRing(peer.peerId, 'none');
      }, RING_TIMEOUT_MS);
      return () => window.clearTimeout(timer);
    }
    if (peer.ring === 'declined') {
      const timer = window.setTimeout(() => setPeerRing(peer.peerId, 'none'), RING_DECLINED_RESET_MS);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [peer.ring, peer.peerId, socket, setPeerRing]);

  const ring = (): void => {
    setPeerRing(peer.peerId, 'outgoing');
    socket.emit('call:ring', { to: peer.peerId }, (res) => {
      if (!res.ok) setPeerRing(peer.peerId, 'none');
    });
  };

  const cancel = (): void => {
    socket.emit('call:cancel', { to: peer.peerId });
    setPeerRing(peer.peerId, 'none');
  };

  return (
    <div ref={ref} className="bubble" data-player={peer.peerId}>
      {peer.ring === 'outgoing' ? (
        <button className="bubble__ring bubble__ring--outgoing" onClick={cancel} title="Klingeln abbrechen">
          Klingelt…
        </button>
      ) : peer.ring === 'declined' ? (
        <span className="bubble__ring bubble__ring--declined">Abgelehnt</span>
      ) : (
        <button className="bubble__ring" onClick={ring} title={`${peer.displayName} anklingeln`}>
          🔔 Anklingeln
        </button>
      )}
    </div>
  );
}
